import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Check, Truck, Heart, PackageCheck } from "lucide-react";
import { MugProgress, MugDeliveryNote, MugCountdown } from "@/components/merch/MugProgress";
import { toast } from "sonner";
import { api, formatErr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MugGallery } from "@/components/merch/MugGallery";
import { COUNTRIES, HOME, mugDaysText } from "@/lib/countries";

const IMAGES = ["/merch/tasse-gold.jpg", "/merch/tasse-gold-haende.jpg", "/merch/tasse-gold-geschenk.jpg"];

const Field = ({ label, testId, ...p }) => (
  <label className="block space-y-1">
    <span className="text-xs font-bold text-slate-500">{label}</span>
    <input {...p} className="w-full h-11 rounded-2xl border border-amber-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid={testId} />
  </label>
);

function AddressForm({ gift, onDone }) {
  const { user } = useAuth();
  const a = gift.address || {};
  const [f, setF] = useState({ name: a.name || user?.name || "", street: a.street || "", zip: a.zip || "", city: a.city || "", country: a.country || HOME, note: a.note || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ok = f.name.trim().length >= 3 && f.street.trim().length >= 3 && (f.country === HOME ? /^\d{5}$/.test(f.zip) : /^[A-Za-z0-9][A-Za-z0-9 -]{1,11}$/.test(f.zip.trim())) && COUNTRIES.includes(f.country) && f.city.trim().length >= 2;
  const send = async () => {
    setBusy(true);
    try { await api.post(`/mug-gift/${gift.id}/address`, f); toast.success("Danke! Wir verpacken deine Tasse mit viel Liebe 💛"); onDone(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="mug-address-form">
      <div className="sm:col-span-2"><Field label="Vor- und Nachname" value={f.name} onChange={set("name")} testId="mug-name" autoComplete="name" /></div>
      <div className="sm:col-span-2"><Field label="Straße und Hausnummer" value={f.street} onChange={set("street")} testId="mug-street" autoComplete="street-address" /></div>
      <label className="block space-y-1 sm:col-span-2">
        <span className="text-xs font-bold text-slate-500">Land</span>
        <select value={f.country} onChange={set("country")} data-testid="mug-country" autoComplete="country-name" className="w-full h-11 rounded-2xl border border-amber-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>
      <Field label="PLZ" value={f.zip} onChange={set("zip")} inputMode={f.country === HOME ? "numeric" : "text"} maxLength={f.country === HOME ? 5 : 12} testId="mug-zip" autoComplete="postal-code" />
      <Field label="Ort" value={f.city} onChange={set("city")} testId="mug-city" autoComplete="address-level2" />
      <div className="sm:col-span-2"><Field label="Anmerkung (optional)" value={f.note} onChange={set("note")} maxLength={300} testId="mug-note" /></div>
      <p className="sm:col-span-2 text-[11px] text-slate-400">Wir verschicken weltweit kostenlos. Lieferzeit nach {f.country}: ca. {mugDaysText(f.country)}. Deine Adresse nutzen wir nur für den Versand der Tasse.</p>
      <button type="button" onClick={send} disabled={!ok || busy} className="sm:col-span-2 h-12 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-[#3a2508] font-black disabled:opacity-50" data-testid="mug-address-submit">
        {gift.status === "address_given" ? "Adresse aktualisieren" : "Adresse senden"}
      </button>
    </div>
  );
}

function Shipped({ g, onDone }) {
  const [busy, setBusy] = useState(false);
  const arrived = async () => {
    setBusy(true);
    try { await api.post(`/mug-gift/${g.id}/delivered`); toast.success("Wie schön! Viel Freude mit deiner Tasse ☕💛"); onDone(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <p className="flex items-center gap-2 text-sm text-emerald-700 font-bold flex-wrap" data-testid={`mug-shipped-${g.id}`}><Truck className="w-4 h-4" /> {g.status === "delivered" ? "Deine Tasse ist angekommen" : "Deine Tasse ist unterwegs"}{g.tracking ? <> · Sendungsnummer <span className="font-mono select-all" data-testid={`mug-tracking-number-${g.id}`}>{g.tracking}</span></> : ""}</p>
      {g.status === "shipped" && (
        <button type="button" onClick={arrived} disabled={busy} className="w-full h-12 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-[#3a2508] font-black inline-flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[.98] transition-transform" data-testid={`mug-delivered-${g.id}`}>
          <PackageCheck className="w-5 h-5" /> Angekommen
        </button>
      )}
    </div>
  );
}

function GiftCard({ g, onDone }) {
  return (
    <section className="lux-card rounded-[2rem] p-5 space-y-4" data-testid={`mug-gift-${g.id}`}>
      <div className="flex items-center gap-3">
        <img src="/merch/tasse-gold.jpg" alt="" className="w-14 h-14 rounded-2xl object-cover" />
        <div className="min-w-0 flex-1"><p className="font-extrabold text-slate-800">Dankeschön-Tasse „Black &amp; Gold“</p><p className="text-xs text-slate-400">Geschenkt am {fmtDate(g.created_at)}</p></div>
        <span className="shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800" data-testid={`mug-gift-status-${g.id}`}>{g.status_label}</span>
      </div>
      <MugProgress g={g} />
      <MugCountdown g={g} />
      {["shipped", "delivered"].includes(g.status) ? <Shipped g={g} onDone={onDone} />
        : g.status === "in_production" ? <p className="text-sm font-bold text-amber-700" data-testid={`mug-in-production-${g.id}`}>🏭 Deine Tasse wird gerade extra für dich produziert – Lieferzeit {mugDaysText(g.address?.country)}. Du bekommst eine Push-Nachricht, sobald sie unterwegs ist.</p>
          : <AddressForm gift={g} onDone={onDone} />}
    </section>
  );
}

export default function MugThanks() {
  const [d, setD] = useState(null);
  const load = () => api.get("/mug-gift").then(({ data }) => setD(data)).catch(() => setD(false));
  useEffect(() => { load(); }, []);
  if (!d) return <p className="text-sm text-slate-400" data-testid="mug-loading">Lädt …</p>;
  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-10" data-testid="mug-thanks-page">
      <header className="rounded-[2rem] bg-[#121212] text-white overflow-hidden ring-1 ring-amber-300/25">
        <div className="grid grid-cols-3">{IMAGES.map((src) => <img key={src} src={src} alt="" className="w-full aspect-square object-cover" />)}</div>
        <div className="p-6 space-y-2">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-amber-300 flex items-center gap-1.5"><Gift className="w-4 h-4" /> Dankeschön für Projekt Unterstützer</p>
          <h1 className="text-3xl sm:text-4xl font-display font-black text-amber-300">Tasse „Black &amp; Gold“</h1>
          <p className="text-sm text-white/75">Wer die Community mit <b className="text-amber-300">mindestens {d.threshold} € auf einmal</b> unterstützt, bekommt von uns diese Tasse mit unserem goldenen Herz-Logo geschenkt – als persönliches Dankeschön, kostenlos nach Hause geschickt. 💛</p>
        </div>
      </header>
      <MugDeliveryNote />
      {d.gifts.length ? d.gifts.map((g) => <GiftCard key={g.id} g={g} onDone={load} />) : (
        <section className="lux-card rounded-[2rem] p-5 space-y-3" data-testid="mug-no-gift">
          <p className="flex items-center gap-2 font-extrabold text-slate-800"><Heart className="w-5 h-5 text-rose-500" /> So bekommst du die Tasse</p>
          <ul className="space-y-1.5 text-sm text-slate-600">
            {["Überweise mindestens 100 € auf einmal – mit deinem persönlichen Code als Verwendungszweck", "Melde deine Überweisung in der App – wir bestätigen sie persönlich", "Du bekommst eine Nachricht und gibst hier deine Lieferadresse ein", "Wir geben deine Tasse extra für dich in Produktion – Lieferzeit 14–21 Tage", "Beim Versand bekommst du eine Push-Nachricht mit Sendungsnummer", "Ist die Tasse da, tippst du auf „Angekommen“ – alles direkt in der App, ganz ohne E-Mails"].map((t) => <li key={t} className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" /> {t}</li>)}
          </ul>
          <Link to="/tasse" className="inline-flex items-center h-11 px-5 rounded-full border border-amber-300 text-amber-700 text-sm font-bold mr-2" data-testid="mug-present-link">Alles zur Tasse</Link>
          <Link to="/community-unterstuetzen#bank-support" className="inline-flex items-center h-11 px-5 rounded-full pink-gradient text-white text-sm font-bold" data-testid="mug-support-link">Projekt unterstützen</Link>
        </section>
      )}
      <MugGallery d={d} onChange={load} />
    </div>
  );
}
